import React, { HTMLAttributes, useEffect, useState } from 'react';
import cn from '@/utils/cn';
import { VariantProps } from 'class-variance-authority';
import { avatarVariants } from './avatarVariants';
import { CheckIcon } from '@/components/Icons';
import DefaultAvatar from '@/assets/Avatar.png';

const alphanumericRegex = /^[a-zA-Z]*$/;

interface AvatarIconProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  key: string;
  imgSrc?: string | null;
  avatarTextSize?: string | null;
  name: string;
  checkIcon?: boolean;
  useDefaultAvatar?: boolean;
}

const InitialsStyle = cn([
  'flex',
  'items-center',
  'justify-center',
  'text-emerge-navy',
  `bg-emerge-teal-3`,
  'm-auto'
]);

const AvatarIcon: React.FC<AvatarIconProps> = ({
  imgSrc,
  name,
  className,
  variant,
  avatarTextSize,
  checkIcon,
  useDefaultAvatar = false
}) => {
  const resolvedSrc = imgSrc ?? (useDefaultAvatar ? DefaultAvatar : null);

  const [imgFailedToLoad, setImgFailedToLoad] = useState(false);

  // Reset the failure flag whenever the source changes so a new/updated photo gets a fresh try
  useEffect(() => {
    setImgFailedToLoad(false);
  }, [resolvedSrc]);

  if (!name && !imgSrc && !useDefaultAvatar) {
    return (
      <div className={cn(avatarVariants({ variant, className }))}>
        <div className={InitialsStyle} />
      </div>
    );
  }

  const getInitials = (name: string) => {
    const splitName = name.split(' ');
    const hasOneName = splitName.length === 1;
    if (hasOneName) {
      if (splitName[0][0]) {
        return splitName[0][0].toUpperCase();
      }
      return splitName[0][0];
    }
    const initials = splitName
      .map((name) => {
        if (!alphanumericRegex.test(name[0])) {
          return;
        }
        if (name[0]) {
          return name[0].toUpperCase();
        }
        return;
      })
      .filter(Boolean)
      .join('');
    if (initials.length > 2) {
      return initials.substring(0, 2);
    }
    return initials;
  };

  const initials = getInitials(name);

  return (
    <div className={cn(avatarVariants({ variant, className }), `grid`)}>
      {resolvedSrc && !imgFailedToLoad ? (
        <img
          className={cn(avatarVariants({ variant, className }))}
          src={resolvedSrc}
          alt={name}
          style={{ gridRow: '1 / -1', gridColumn: '1 / -1' }}
          onError={() => setImgFailedToLoad(true)}
        />
      ) : (
        <div
          className={cn(InitialsStyle, avatarVariants({ variant, className }))}
          style={{ gridRow: '1 / -1', gridColumn: '1 / -1' }}>
          <span className={`text-${avatarTextSize ?? 'xs'} font-medium`}>{initials}</span>
        </div>
      )}
      {checkIcon && (
        <div
          className={`bg-emerge-teal-3 rounded-full flex justify-center items-center wrap self-end justify-self-end`}
          style={{ gridRow: '1 / -1', gridColumn: '1 / -1', height: '30%', aspectRatio: '1/1' }}>
          <CheckIcon className={`h-[50%] w-[50%]`} />
        </div>
      )}
    </div>
  );
};

export default AvatarIcon;
